import { Button } from './Button';

export interface ScreenEmptyAction {
  label: string;
  onClick: () => void;
}

export interface ScreenEmptyProps {
  /** Short headline (optional — body may carry the whole sentence). */
  title?: string;
  /** One-sentence explanation or next-step prompt. */
  body?: string;
  /** Primary next action; every empty state should offer exactly one. */
  primary?: ScreenEmptyAction;
  /** Optional secondary affordance (e.g. "Load Demo"). */
  secondary?: ScreenEmptyAction;
  /** Optional extra className for context-specific tweaks. */
  className?: string;
}

/**
 * Shared empty-state affordance. Renders a centred, padded block with a
 * sentence (title and/or body) plus an inline primary and optional
 * secondary CTA, so every first-run "nothing here yet" moment routes the
 * user to their next step instead of leaving them stranded.
 *
 * Owns the `.screen-empty` styling (centred, padded, max inline measure),
 * closing the audit observation that `.screen-empty` had no CSS rule.
 */
export function ScreenEmpty({ title, body, primary, secondary, className = '' }: ScreenEmptyProps) {
  return (
    <div className={`screen-empty ${className}`.trim()} role="status">
      {title ? <p className="screen-empty-title">{title}</p> : null}
      {body ? <p className="screen-empty-body">{body}</p> : null}
      {primary || secondary ? (
        <div className="screen-empty-actions">
          {primary ? (
            <Button variant="primary" size="sm" onClick={primary.onClick}>
              {primary.label}
            </Button>
          ) : null}
          {secondary ? (
            <Button variant="secondary" size="sm" onClick={secondary.onClick}>
              {secondary.label}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
